// Ra lệnh bằng chữ (gói H, §2.4): ô lệnh → POST /api/jobs/draft → thẻ XEM TRƯỚC (kịch bản, tham số, bot, giãn cách, canary,
// độ tin cậy, gợi ý) → người dùng bấm Phát (qua /api/jobs/preview rồi /api/jobs như form thường) hoặc Sửa (mở form lệnh có
// sẵn spec) hoặc Huỷ. Không bao giờ tự chạy. Nút Soạn khoá trong lúc gọi (bấm 2 lần = 1 request).
import { api, errMsg } from "../api";
import { busy, clear, gen, h, toast } from "../dom";
import { chiaDeuText, modeText } from "../format";
import { loadBots, store } from "../store";
import type { Bot, Job, JobSpec, PreviewResult } from "../types";
import { ganTenKbChu, tenKbDaBiet } from "../ten-kb";
import { moHopDangNgay, taiDsVideo } from "./kho-video-hop"; // (06/10/2026) nút "Đăng video thử ngay" — hộp riêng ở kho-video-hop.ts

export interface DraftResult {
  spec: JobSpec | null;
  moTa: string;
  doTinCay: number;
  goiY?: string;
  goiYKichBan?: { id: string; ten: string }[]; // N3: kịch bản sơ đồ của mình gần giống câu đã gõ
}

export const LOW_CONFIDENCE = 0.5;

/** Câu xem trước: "Sẽ chạy “Tên” ở chế độ chạy thử với tham số a=1, b=2 trên 2 bot: A, B — giãn cách 180 giây — chạy thử trên 1 bot trước: tắt". */
export function previewSentence(spec: JobSpec, nameOf: (id: string) => string, tenKichBan: (bundleName: string) => string = (b) => b): string {
  const params = Object.entries(spec.params).map(([k, v]) => `${k}=${v}`);
  const parts = [`Sẽ chạy “${tenKichBan(spec.bundleName)}” ở chế độ ${modeText(spec.mode).toLowerCase()}`];
  if (spec.kind === "chia_deu") parts.push(`chia đều ${spec.list?.length ?? 0} phần tử của ${spec.listParam ?? "?"} (${chiaDeuText(spec.list?.length ?? 0, spec.botIds.length)})${params.length ? `, tham số chung ${params.join(", ")}` : ""}`);
  else parts.push(params.length ? `với tham số ${params.join(", ")}` : "không có tham số");
  parts.push(`trên ${spec.botIds.length} bot: ${spec.botIds.map(nameOf).join(", ")}`);
  parts.push(`— giãn cách ${spec.gianCachS} giây`);
  parts.push(`— chạy 1 bot trước: ${spec.canary.on ? "bật" : "tắt"}`);
  return parts.join(" ");
}

/** Mã yêu cầu chống bấm 2 lần / F5 cho POST /api/kich-ban (máy chủ nhận [A-Za-z0-9_-]{6,64}). */
function maYeuCauMoi(): string {
  return `lenh${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export async function renderLenh(root: HTMLElement, _p: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  const alive = gen("lenh");
  root.append(h("h1", {}, "Ra lệnh bằng chữ"));
  let bots: Bot[] = [];
  try {
    bots = await loadBots();
  } catch (e) {
    // Không tải được bot thì vẫn cho ra lệnh (server tự chọn bot); chỉ báo nhẹ.
    toast(`Không tải được danh sách bot: ${errMsg(e)}`, "warn");
  }
  if (!alive()) return () => {};
  const nameOf = (id: string): string => store.bots.get(id)?.name ?? bots.find((b) => b.id === id)?.name ?? id;

  // Bot đã tick: từ query (nút "Ra lệnh bằng chữ" ở bảng bot) hoặc store.ticked.
  const ticked = (query.get("botIds") ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  if (!ticked.length) ticked.push(...store.ticked);
  const tickedValid = ticked.filter((id) => bots.some((b) => b.id === id));

  const text = h("textarea", { "data-testid": "lenh-text", rows: 4, title: "Gõ việc cần làm bằng tiếng Việt; Ctrl+Enter = Soạn", placeholder: "Ví dụ: tìm nhóm sketchup trên bot Hà Nội — hoặc: bình luận \"Xin chào\" vào 3 bot, chạy thật" });
  const soan = h("button", { type: "button", class: "pri", title: "Gửi câu lệnh cho AI để soạn thành lệnh chạy; chỉ xem trước, chưa chạy gì", "data-testid": "lenh-soan" }, "Soạn");
  const huy = h("button", { type: "button", title: "Bỏ bản xem trước, không phát gì", "data-testid": "lenh-huy", hidden: true }, "Huỷ");
  const errBox = h("div", { class: "err-box", hidden: true, "data-testid": "lenh-error" });
  // Đăng video thử ngay (06/10/2026): chọn video trong Kho video + bot + nền tảng → kịch bản một bước Riêng tư đi qua cổng Chạy thử → Chạy thật (kho-video-hop.ts moHopDangNgay).
  const dangVideoThu = h("button", { type: "button", class: "kv-nut", title: "Chọn video trong Kho video, bot và nền tảng — bot đăng ở chế độ Riêng tư để bạn kiểm tra", "data-testid": "lenh-dang-video-thu" }, "▶ Đăng video");
  dangVideoThu.addEventListener("click", () => void busy(dangVideoThu, "Đang mở…", async () => {
    let ds: Awaited<ReturnType<typeof taiDsVideo>> = [];
    try { ds = await taiDsVideo(); } catch (e) { toast(`Không tải được Kho video: ${errMsg(e)}`, "err"); return; }
    if (!ds.length) { toast("Kho video đang trống — tải video lên ở trang Kho video trước", "warn"); return; }
    const duong = await moHopDangNgay({ dsVideo: ds });
    if (duong) location.hash = duong;
  }));
  const result = h("div", { "data-testid": "lenh-result" });

  // N11 (phương án 4.4): trang này chỉ còn trong Chế độ kỹ thuật — chỉ đường về lối chính cho người lạc vào (Kịch bản: ô Mong muốn; Trợ lý).
  root.append(h("div", { class: "dai-chuyen", role: "note", "data-testid": "lenh-dan-duong" }, h("span", { class: "grow" },
    "Chế độ kỹ thuật: trang này chỉ ghép câu lệnh với kịch bản ĐÃ LƯU (học trên bot). Boss thường không thấy trang này — muốn tạo kịch bản mới bằng lời, gõ mong muốn ở ",
    h("a", { href: "#/kich-ban?moTa=1", title: "Mở trang Kịch bản, con trỏ ở ô Mong muốn" }, "trang Kịch bản"),
    "; muốn chạy kịch bản có sẵn bằng lời, hỏi ",
    h("a", { href: "#/tro-ly", title: "Mở Trợ lý — hỏi bằng lời, Trợ lý đề xuất việc nên làm, bạn bấm mới làm" }, "Trợ lý"),
    " (ví dụ “chạy kịch bản X trên bot Y”).")));
  root.append(
    h("div", { class: "card" },
      h("p", { class: "small muted" }, "Mô tả việc cần làm bằng tiếng Việt. AI tìm một kịch bản ĐÃ CÓ trong kho kịch bản học trên bot rồi điền tham số; bạn xem trước rồi mới bấm Phát — không có gì tự chạy. Chưa có kịch bản phù hợp thì có nút tạo kịch bản mới ngay từ câu bạn gõ."),
      h("label", { class: "f" }, h("span", {}, "Lệnh"), text),
      tickedValid.length
        ? h("p", { class: "small muted", "data-testid": "lenh-ticked" }, `Bot đã tick: ${tickedValid.map(nameOf).join(", ")} (AI sẽ ưu tiên các bot này)`)
        : h("p", { class: "small muted", "data-testid": "lenh-ticked" }, "Chưa tick bot nào — AI sẽ chọn trong các bot trực tuyến của bạn (có thể nêu tên bot trong câu)."),
      h("div", { class: "row" }, soan, huy, h("span", { class: "grow" }), dangVideoThu, h("a", { class: "btn", href: "#/bots", title: "Về trang Bot" }, "Quay lại")),
      errBox),
    result);

  function showError(msg: string): void {
    errBox.textContent = msg;
    errBox.hidden = false;
  }
  function clearResult(): void {
    clear(result);
    huy.hidden = true;
  }

  function renderResult(d: DraftResult, cauDaGo: string): void {
    clear(result);
    huy.hidden = false;
    // N3: không ghép được kịch bản → không phải "AI không chắc" (độ tin cậy 0% vô nghĩa) mà là chưa có kịch bản: đưa lối ra tạo kịch bản.
    const low = !!d.spec && d.doTinCay < LOW_CONFIDENCE;
    const pct = `${Math.round(d.doTinCay * 100)}%`;
    const card = h("div", { class: `card${low ? " lenh-low" : ""}${d.spec ? "" : " lenh-khong-khop"}`, "data-testid": "lenh-preview", style: low ? "border: 2px solid var(--warn)" : "" });
    card.append(h("h3", {}, d.spec ? "Xem trước" : "Chưa có kịch bản nào khớp lệnh này"));
    if (low) card.append(h("p", { class: "warn", "data-testid": "lenh-canh-bao" }, "AI không chắc, hãy kiểm tham số"));
    if (d.spec) {
      const spec = d.spec;
      const cau = h("p", { class: "wrap", "data-testid": "lenh-cau" }, previewSentence(spec, nameOf));
      ganTenKbChu(cau, spec.bundleName, () => previewSentence(spec, nameOf, tenKbDaBiet)); // kịch bản sơ đồ kb-… → tên
      card.append(cau);
      const rows = Object.entries(spec.params).map(([k, v]) => h("tr", {}, h("td", { class: "nowrap" }, h("code", {}, k)), h("td", { class: "wrap" }, v || h("span", { class: "muted" }, "(trống)"))));
      if (rows.length) card.append(h("div", { class: "tbl params" }, h("table", { "data-testid": "lenh-params" }, h("thead", {}, h("tr", {}, h("th", {}, "Tham số"), h("th", {}, "Giá trị"))), h("tbody", {}, ...rows))));
      if (spec.kind === "chia_deu" && spec.list?.length) {
        card.append(h("details", {}, h("summary", { class: "small" }, `Danh sách ${spec.list.length} phần tử`), h("pre", { class: "small wrap", style: "white-space:pre-wrap;max-height:200px;overflow:auto" }, spec.list.join("\n"))));
      }
      card.append(h("ul", { class: "small", "data-testid": "lenh-bots" }, ...spec.botIds.map((id) => h("li", { dataset: { botId: id } }, nameOf(id)))));
      if (spec.mode === "live") card.append(h("p", { class: "small warn" }, "Chế độ chạy thật: bot sẽ làm thật. Bước rủi ro cao vẫn dừng chờ duyệt."));
    }
    card.append(h("p", { class: "small muted wrap", "data-testid": "lenh-mo-ta" }, d.moTa));
    if (d.spec) card.append(h("p", { class: "small", "data-testid": "lenh-tin-cay", title: "AI tự đánh giá mức chắc chắn đã hiểu đúng câu lệnh; dưới 50% nên kiểm lại tham số" }, "Độ tin cậy: ", h("b", { class: low ? "warn" : "ok" }, pct)));
    if (d.goiY) card.append(h("p", { class: `small wrap${d.spec ? " warn" : ""}`, "data-testid": "lenh-goi-y" }, d.spec ? "Gợi ý: " : "", d.goiY));
    const phatErr = h("div", { class: "err-box", hidden: true, "data-testid": "lenh-phat-error" });
    const phat = h("button", { type: "button", class: d.spec ? "pri" : "", title: d.spec ? "Phát lệnh đúng như bản xem trước tới các bot (máy chủ kiểm tra lại, bot bị từ chối sẽ bị bỏ qua)" : "Chưa có kịch bản khớp lệnh này để phát", "data-testid": "lenh-phat", disabled: !d.spec }, "Phát");
    const sua = h("button", { type: "button", title: "Mở form phát lệnh với nội dung này điền sẵn để chỉnh tay trước khi phát", "data-testid": "lenh-sua", disabled: !d.spec }, "Sửa trong form");
    const huy2 = h("button", { type: "button", title: "Bỏ bản xem trước, không phát gì", "data-testid": "lenh-huy-2", onclick: clearResult }, "Huỷ");
    // N3: lối ra khi không có kịch bản khớp — tạo kịch bản sơ đồ với mong muốn = câu vừa gõ (AI viết, người dùng xem rồi mới chạy).
    let taoKb: HTMLButtonElement | null = null;
    if (!d.spec) {
      taoKb = h("button", { type: "button", class: "pri", title: "Tạo kịch bản mới: AI viết các bước theo đúng câu bạn vừa gõ; bạn xem, sửa rồi mới chạy (chưa chạy gì)", "data-testid": "lenh-tao-kb" }, "Tạo kịch bản từ câu này");
      const yeuCauId = maYeuCauMoi();
      taoKb.addEventListener("click", () => void busy(taoKb!, "Đang tạo…", async () => {
        phatErr.hidden = true;
        try {
          const kb = await api<{ id: string }>("POST", "/api/kich-ban", { yeuCau: cauDaGo, yeuCauId });
          location.hash = `/kich-ban/${encodeURIComponent(kb.id)}`;
        } catch (e) { phatErr.textContent = `Không tạo được kịch bản: ${errMsg(e)}`; phatErr.hidden = false; }
      }));
      if (d.goiYKichBan?.length) {
        card.append(h("div", { class: "small", "data-testid": "lenh-kb-gan" }, h("p", { style: "margin:6px 0 2px" }, "Hoặc mở kịch bản có sẵn gần giống câu này (chạy ở trang kịch bản):"),
          h("ul", { style: "margin:0;padding-left:18px" }, ...d.goiYKichBan.map((k) => h("li", {}, h("a", { href: `#/kich-ban/${encodeURIComponent(k.id)}`, title: "Mở kịch bản này để xem và chạy", "data-testid": "lenh-kb-gan-link" }, k.ten))))));
      }
    }
    // Không có kịch bản khớp: nút "Tạo kịch bản từ câu này" đứng TRƯỚC (lối ra chính, màn hẹp không bị đẩy xuống dòng cuối).
    card.append(phatErr, ...(taoKb ? [h("div", { class: "row", style: "margin-top:8px" }, taoKb)] : []), h("div", { class: "row end", style: "margin-top:8px" }, huy2, sua, phat));
    result.append(card);

    sua.addEventListener("click", () => {
      if (!d.spec) return;
      store.draftSpec = d.spec;
      location.hash = `/jobs/new?botIds=${encodeURIComponent(d.spec.botIds.join(","))}`;
    });
    phat.addEventListener("click", () => void busy(phat, "Đang phát…", async () => {
      if (!d.spec) return;
      phatErr.hidden = true;
      const spec: JobSpec = { ...d.spec, botIds: [...d.spec.botIds], params: { ...d.spec.params }, canary: { ...d.spec.canary } };
      try {
        const pv = await api<PreviewResult>("POST", "/api/jobs/preview", spec);
        const okIds = pv.runs.filter((r) => r.ok).map((r) => r.botId);
        if (!okIds.length) {
          phatErr.textContent = `Không bot nào chạy được: ${pv.runs.map((r) => `${r.botName}: ${r.reason ?? "máy chủ từ chối"}`).join("; ")}`;
          phatErr.hidden = false;
          return;
        }
        spec.botIds = spec.botIds.filter((id) => okIds.includes(id));
        if (spec.canary.botId && !spec.botIds.includes(spec.canary.botId)) delete spec.canary.botId;
        const job = await api<Job>("POST", "/api/jobs", spec);
        const skipped = pv.runs.filter((r) => !r.ok);
        toast(skipped.length ? `Đã phát lệnh cho ${okIds.length} bot; bỏ ${skipped.length} bot (${skipped.map((r) => r.botName).join(", ")})` : `Đã phát lệnh cho ${job.runs.length} bot`);
        store.ticked.clear();
        location.hash = `/jobs/${encodeURIComponent(job.id)}`;
      } catch (e) {
        phatErr.textContent = errMsg(e);
        phatErr.hidden = false;
      }
    }));
  }

  soan.addEventListener("click", () => void busy(soan, "Đang soạn…", async () => {
    errBox.hidden = true;
    const t = text.value.trim();
    if (!t) { showError("Chưa nhập lệnh"); return; }
    clearResult();
    try {
      const body: { text: string; botIds?: string[] } = { text: t };
      if (tickedValid.length) body.botIds = tickedValid;
      const d = await api<DraftResult>("POST", "/api/jobs/draft", body);
      if (!alive()) return;
      renderResult(d, t);
    } catch (e) {
      if (!alive()) return;
      showError(errMsg(e));
    }
  }));
  huy.addEventListener("click", () => { clearResult(); errBox.hidden = true; });
  text.addEventListener("keydown", (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") soan.click(); });
  text.focus();
  return () => {};
}
